/**
 * Keyword boxes on exported PDF page images.
 *
 * The export gather records, per PDF drug, where each query term sits on each
 * page (ExportDrugPdf.marks: 0-1 fractions of the page + the term's colour).
 * The live viewer and the Share snapshot draw those boxes OVER the page image;
 * a Word / Excel / HTML file can't overlay anything, so the boxes are painted
 * INTO the image while it is shrunk for the export — by whichever encoder runs
 * (browser Web Worker, browser canvas, or the server's sharp). Same look as the
 * snapshot's SnapMarks: the term colour, multiplied at 55 % so the text under
 * it stays readable.
 *
 * No React, no DOM globals: imported by the browser image worker and the
 * server export worker too.
 */

export interface PageMark {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  color: string;
}

/** A mark in pixels on an image of a given size. */
export interface MarkBox {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
}

/** SnapMarks' opacity (multiply blend). */
export const MARK_OPACITY = 0.55;
const DEFAULT_COLOR = '#fde047';
// Colours reach an SVG on the server: accept only plain hex / rgb() values.
const SAFE_COLOR = /^(#[0-9a-f]{3,8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\))$/i;

export function safeMarkColor(color: string | undefined | null): string {
  return color && SAFE_COLOR.test(color.trim()) ? color.trim() : DEFAULT_COLOR;
}

const clamp01 = (v: number) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

/** The marks as pixel boxes on a `width` x `height` image: clamped to the
 *  image, at least 1 px, empty or inverted boxes dropped. */
export function markBoxes(marks: readonly PageMark[] | undefined, width: number, height: number): MarkBox[] {
  const out: MarkBox[] = [];
  for (const m of marks ?? []) {
    const x0 = clamp01(Math.min(m.x0, m.x1)), x1 = clamp01(Math.max(m.x0, m.x1));
    const y0 = clamp01(Math.min(m.y0, m.y1)), y1 = clamp01(Math.max(m.y0, m.y1));
    if (x1 <= x0 || y1 <= y0) continue;
    const x = Math.floor(x0 * width);
    const y = Math.floor(y0 * height);
    out.push({
      x,
      y,
      width: Math.max(1, Math.ceil(x1 * width) - x),
      height: Math.max(1, Math.ceil(y1 * height) - y),
      color: safeMarkColor(m.color),
    });
  }
  return out;
}

/** The marks of each page, keyed by page_num (as ExportDrugPdf.marks[].page). */
export function marksByPage(marks: readonly (PageMark & { page: number })[] | undefined): Map<number, PageMark[]> {
  const by = new Map<number, PageMark[]>();
  for (const { page, ...m } of marks ?? []) {
    const list = by.get(page);
    if (list) list.push(m);
    else by.set(page, [m]);
  }
  return by;
}

/** The 2D-context calls paintMarks needs (canvas or OffscreenCanvas). */
interface MarkContext {
  globalAlpha: number;
  globalCompositeOperation: string;
  fillStyle: string | CanvasGradient | CanvasPattern;
  fillRect(x: number, y: number, w: number, h: number): void;
  save(): void;
  restore(): void;
}

/** Paint the marks onto a canvas already holding the page at width x height. */
export function paintMarks(ctx: MarkContext, marks: readonly PageMark[] | undefined, width: number, height: number): void {
  const boxes = markBoxes(marks, width, height);
  if (!boxes.length) return;
  ctx.save();
  ctx.globalCompositeOperation = 'multiply';
  ctx.globalAlpha = MARK_OPACITY;
  for (const b of boxes) {
    ctx.fillStyle = b.color;
    ctx.fillRect(b.x, b.y, b.width, b.height);
  }
  ctx.restore();
}

/** The colour as [r, g, b] (0-255); yellow when it can't be read. */
export function parseMarkColor(color: string): [number, number, number] {
  const c = safeMarkColor(color);
  const hex = /^#([0-9a-f]{3,8})$/i.exec(c)?.[1];
  if (hex) {
    const full = hex.length <= 4 ? [...hex].map((h) => h + h).join('') : hex;
    return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [number, number, number];
  }
  const rgb = c.match(/\d{1,3}/g);
  if (rgb && rgb.length >= 3) return rgb.slice(0, 3).map((v) => Math.min(255, Number(v))) as [number, number, number];
  return parseMarkColor(DEFAULT_COLOR);
}

/** Multiplying by colour c at opacity a is the same as multiplying by the
 *  solid colour 1 - a(1 - c): base * (1 - a) + a * base * c = base * (1 - a(1 - c)).
 *  The server uses it because libvips' multiply mishandles a translucent
 *  layer (it darkens the whole box); with a solid layer the result matches
 *  the browser canvas and the snapshot overlay, and dark text stays dark. */
export function multiplyColorAt(color: string, opacity = MARK_OPACITY): string {
  const [r, g, b] = parseMarkColor(color).map((v) => Math.round(255 - opacity * (255 - v)));
  return `rgb(${r},${g},${b})`;
}

/** The marks as an SVG layer of the image's size, to composite with a
 *  multiply blend (solid colours: see multiplyColorAt). Empty string when
 *  there is nothing to draw. */
export function marksSvg(marks: readonly PageMark[] | undefined, width: number, height: number): string {
  const boxes = markBoxes(marks, width, height);
  if (!boxes.length) return '';
  const rects = boxes
    .map((b) => `<rect x="${b.x}" y="${b.y}" width="${b.width}" height="${b.height}" fill="${multiplyColorAt(b.color)}"/>`)
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">${rects}</svg>`;
}
